import React from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@/nova/components';
import { Sheet, useSheetDismiss } from '@/nova/components/Sheet';

import { Bar, Glyph, Status, SignOutLink, ModalBody, FooterRow } from './ImpersonationBanner.styles';

export type ImpersonationBannerProps = {
  /**
   * The support agent (admin) running this session — `admin.username` from the
   * `/auth/check` response. When omitted, a generic notice is shown.
   */
  username?: string;
  /**
   * Confirm + perform the sign-out. Revokes THIS device's session only (see
   * MainLayout) — the owner's other sessions are untouched. The strip stays
   * presentational: it owns the confirm modal, this performs the action.
   */
  onSignOut: () => void;
  /** Sign-out in flight — disables the modal actions to prevent a double submit. */
  signingOut?: boolean;
};

/** Headset glyph — the support agent. Inherits the bar's ink via currentColor. */
const HeadsetIcon = (): React.ReactElement => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true" focusable="false">
    <path d="M4 12.5a8 8 0 0 1 16 0" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
    <rect x="3" y="12" width="4.2" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.8" />
    <rect x="16.8" y="12" width="4.2" height="7" rx="1.6" stroke="currentColor" strokeWidth="1.8" />
    <path d="M20 19v.5a3 3 0 0 1-3 3h-2.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
  </svg>
);

/**
 * Studio-wide notice shown when a support agent is signed in AS the account owner
 * (impersonation). The whole strip is a button: clicking it opens a confirm dialog
 * to end the support session. Forwards a ref so MainLayout can measure the strip's
 * height (`--ows-imp-offset`) and push the chrome down by exactly that much.
 */
export const ImpersonationBanner = React.forwardRef<HTMLButtonElement, ImpersonationBannerProps>(
  ({ username, onSignOut, signingOut = false }, ref): React.ReactElement => {
    const { t } = useTranslation();
    const [confirmOpen, setConfirmOpen] = React.useState(false);
    const name = username?.trim();
    const close = React.useCallback((): void => setConfirmOpen(false), []);
    // Dismiss through the overlay so the dialog slides out; wired straight to `close` it
    // would unmount on the spot and blink away.
    const { closeHandle, dismiss } = useSheetDismiss(close);

    return (
      <>
        <Bar ref={ref} type="button" aria-haspopup="dialog" onClick={() => setConfirmOpen(true)}>
          <Glyph>
            <HeadsetIcon />
          </Glyph>
          {name ? (
            <Status
              tag="span"
              text="Support agent ({{username}}) currently signed in."
              placeholders={{ username: name }}
            />
          ) : (
            <Status tag="span" text="Support agent currently signed in." />
          )}
          <SignOutLink tag="span" text="Sign out" />
        </Bar>
        {confirmOpen && (
          <Sheet
            // Wrapped in t() so the title key is extractable (Overlay prop literals aren't harvested).
            title={t('Sign out of this account?')}
            onClose={close}
            closeHandle={closeHandle}
            footer={
              <FooterRow>
                <Button variant="outline" label={t('Cancel')} onClick={dismiss} disabled={signingOut} />
                <Button variant="primary" label={t('Sign out')} onClick={onSignOut} disabled={signingOut} />
              </FooterRow>
            }
          >
            <ModalBody
              tag="paragraph"
              text="This signs out only this support agent session. Any other sessions for this account stay signed in."
            />
          </Sheet>
        )}
      </>
    );
  },
);

ImpersonationBanner.displayName = 'ImpersonationBanner';

export default ImpersonationBanner;
